'use client'

import React, {useEffect, useState} from 'react'
import {cn} from './cn'
import {useRetainedMotionState} from './useRetainedMotionState'

/** Fades content in on mount; key the element to fade each swapped step or view. Browsers without @starting-style show it at once. */
export const FADE_IN_CLASS = 'transition-opacity duration-normal ease-out starting:opacity-0'

const REVEAL_DURATION_MS = 200

interface RevealProps {
    show: boolean
    /** Pass `cond && <X/>` freely: when the content turns empty while collapsing, the last shown content stays on screen. */
    children: React.ReactNode
    className?: string
    /** Also expand when mounted with `show` already true, e.g. a banner that appears with its first render. */
    appear?: boolean
}

/**
 * Expands and collapses conditional content in place instead of pushing the layout below it in one frame.
 * Collapsed content unmounts, so hidden required fields never block form submission.
 */
export function Reveal({show, children, className, appear = false}: RevealProps) {
    const {shouldRender, motionState} = useRetainedMotionState(show, REVEAL_DURATION_MS, appear)
    const [settled, setSettled] = useState(show && !appear)
    const hasChildren = children !== null && children !== undefined && children !== false && children !== ''
    const [shownChildren, setShownChildren] = useState(children)
    if (show && hasChildren && shownChildren !== children) setShownChildren(children)
    // Showing again mid-collapse starts fresh content, as a plain `cond && <X/>` remount would.
    const [shownBefore, setShownBefore] = useState(show)
    const [generation, setGeneration] = useState(0)
    if (show !== shownBefore) {
        setShownBefore(show)
        if (show) setGeneration(value => value + 1)
    }

    useEffect(() => {
        if (motionState !== 'open') {
            setSettled(false)
            return
        }
        // Clip only while the height moves, so focus rings and inline popovers are not cut off once open.
        const timeout = setTimeout(() => setSettled(true), REVEAL_DURATION_MS)
        return () => clearTimeout(timeout)
    }, [motionState])

    if (!shouldRender) return null
    return (
        <div
            data-state={motionState}
            // Entering content stays in the accessibility tree so live regions announce on insertion.
            inert={!show || undefined}
            className="grid transition-[grid-template-rows,opacity] duration-normal ease-out motion-reduce:transition-opacity data-[state=open]:grid-rows-[1fr] data-[state=closed]:grid-rows-[0fr] data-[state=closed]:opacity-0"
        >
            <div className={cn('min-h-0', !settled && 'overflow-hidden')}>
                <div key={generation} className={className}>{hasChildren ? children : shownChildren}</div>
            </div>
        </div>
    )
}
